import React, { useState } from 'react';
import {
  Card,
  Typography,
  Table,
  Switch,
  Input,
  InputNumber,
  Button,
  Space,
  Tag,
  App,
  Skeleton,
  Alert,
  Tooltip,
  Tabs,
  Popconfirm,
} from 'antd';
import {
  SettingOutlined,
  SaveOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  UndoOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { useResponsive } from '../hooks/useResponsive';
import { useAppSelector } from '../hooks/redux';
import { isSupportUser } from '../utils/roleHelpers';
import {
  useGetAllSettingsQuery,
  useGetSettingsCategoriesQuery,
  useUpdateSettingMutation,
  useResetSettingMutation,
  SettingWithValue,
} from '../services/settingsApi';

const { Title, Text } = Typography;

// Setting data types enum
enum SettingDataType {
  String = 0,
  Boolean = 1,
  Integer = 2,
  Decimal = 3,
  Json = 4,
}

const SystemSettings: React.FC = () => {
  // App.useApp() y no el import estatico: bajo React 19 el estatico no dibuja
  const { message } = App.useApp();
  const { isMobile } = useResponsive();
  const { user } = useAppSelector((state) => state.auth);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');

  // Check if user is support
  const isSupport = isSupportUser(user?.email);

  // API hooks
  const { data: settings, isLoading, error, refetch } = useGetAllSettingsQuery();
  useGetSettingsCategoriesQuery();
  const [updateSetting, { isLoading: updating }] = useUpdateSettingMutation();
  const [resetSetting, { isLoading: resetting }] = useResetSettingMutation();

  // If not support user, show access denied
  if (!isSupport) {
    return (
      <Card>
        <Alert
          type="error"
          message="Acceso Denegado"
          description="Esta seccion solo esta disponible para usuarios de soporte."
          showIcon
        />
      </Card>
    );
  }

  const handleEdit = (record: SettingWithValue) => {
    setEditingKey(record.key);
    setEditValue(record.currentValue || record.defaultValue || '');
  };

  const handleSave = async (key: string) => {
    try {
      await updateSetting({ key, value: editValue }).unwrap();
      message.success('Setting actualizado correctamente');
      setEditingKey(null);
      setEditValue('');
    } catch (err: any) {
      message.error(err?.data?.message || 'Error al actualizar el setting');
    }
  };

  const handleCancel = () => {
    setEditingKey(null);
    setEditValue('');
  };

  const handleToggle = async (key: string, checked: boolean) => {
    try {
      await updateSetting({ key, value: checked.toString() }).unwrap();
      message.success('Setting actualizado correctamente');
    } catch (err: any) {
      message.error(err?.data?.message || 'Error al actualizar el setting');
    }
  };

  const handleReset = async (key: string) => {
    try {
      await resetSetting(key).unwrap();
      message.success('Setting restaurado al valor por defecto');
    } catch (err: any) {
      message.error(err?.data?.message || 'Error al restaurar el setting');
    }
  };

  const getDataTypeLabel = (dataType: number) => {
    switch (dataType) {
      case SettingDataType.String:
        return <Tag color="blue">Texto</Tag>;
      case SettingDataType.Boolean:
        return <Tag color="green">Booleano</Tag>;
      case SettingDataType.Integer:
        return <Tag color="orange">Entero</Tag>;
      case SettingDataType.Decimal:
        return <Tag color="purple">Decimal</Tag>;
      case SettingDataType.Json:
        return <Tag color="cyan">JSON</Tag>;
      default:
        return <Tag>Desconocido</Tag>;
    }
  };

  const renderValueEditor = (record: SettingWithValue) => {
    const isEditing = editingKey === record.key;

    if (record.isSystem) {
      return (
        <Tooltip title="Este es un setting del sistema y no puede ser modificado">
          <Text type="secondary">{record.currentValue || record.defaultValue || '-'}</Text>
        </Tooltip>
      );
    }

    if (record.dataType === SettingDataType.Boolean) {
      return (
        <Space>
          <Switch
            checked={record.currentValue?.toLowerCase() === 'true'}
            onChange={(checked) => handleToggle(record.key, checked)}
            loading={updating}
            checkedChildren="Si"
            unCheckedChildren="No"
          />
          {record.hasCustomValue && (
            <Popconfirm
              title="Restaurar valor por defecto"
              description={`¿Restaurar a "${record.defaultValue}"?`}
              onConfirm={() => handleReset(record.key)}
              okText="Si"
              cancelText="No"
            >
              <Button
                type="text"
                size="small"
                icon={<UndoOutlined />}
                loading={resetting}
                danger
              >
                Reset
              </Button>
            </Popconfirm>
          )}
        </Space>
      );
    }

    if (isEditing) {
      return (
        <Space wrap>
          {record.dataType === SettingDataType.Integer ? (
            <InputNumber
              value={parseInt(editValue) || 0}
              onChange={(val) => setEditValue(val?.toString() || '0')}
              style={{ width: 150 }}
            />
          ) : record.dataType === SettingDataType.Decimal ? (
            <InputNumber
              value={parseFloat(editValue) || 0}
              onChange={(val) => setEditValue(val?.toString() || '0')}
              step={0.01}
              style={{ width: 150 }}
            />
          ) : (
            <Input
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              style={{ width: 250 }}
            />
          )}
          <Button
            type="primary"
            size="small"
            icon={<SaveOutlined />}
            onClick={() => handleSave(record.key)}
            loading={updating}
          >
            Guardar
          </Button>
          <Button size="small" onClick={handleCancel}>
            Cancelar
          </Button>
        </Space>
      );
    }

    return (
      <Space>
        <Text strong>{record.currentValue || record.defaultValue || '-'}</Text>
        <Button
          type="link"
          size="small"
          icon={<EditOutlined />}
          onClick={() => handleEdit(record)}
        >
          Editar
        </Button>
        {record.hasCustomValue && (
          <Popconfirm
            title="Restaurar valor por defecto"
            description={`¿Restaurar a "${record.defaultValue}"?`}
            onConfirm={() => handleReset(record.key)}
            okText="Si"
            cancelText="No"
          >
            <Button
              type="text"
              size="small"
              icon={<UndoOutlined />}
              loading={resetting}
              danger
            >
              Reset
            </Button>
          </Popconfirm>
        )}
      </Space>
    );
  };

  const columns = [
    {
      title: 'Setting',
      dataIndex: 'name',
      key: 'name',
      width: 250,
      render: (text: string, record: SettingWithValue) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          <Text code style={{ fontSize: '11px' }}>{record.key}</Text>
          {record.description && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.description}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Tipo',
      dataIndex: 'dataType',
      key: 'dataType',
      width: 100,
      render: (dataType: number) => getDataTypeLabel(dataType),
    },
    {
      title: 'Valor Actual',
      key: 'value',
      width: 350,
      render: (_: any, record: SettingWithValue) => renderValueEditor(record),
    },
    {
      title: 'Valor Default',
      dataIndex: 'defaultValue',
      key: 'defaultValue',
      width: 120,
      render: (text: string, record: SettingWithValue) => (
        <Tooltip title="Valor por defecto definido en el sistema">
          <Text type="secondary">
            {record.dataType === SettingDataType.Boolean
              ? (text?.toLowerCase() === 'true' ? 'Si' : 'No')
              : text || '-'
            }
          </Text>
        </Tooltip>
      ),
    },
    {
      title: 'Estado',
      key: 'status',
      width: 130,
      render: (_: any, record: SettingWithValue) => (
        <Space direction="vertical" size={0}>
          {record.hasCustomValue ? (
            <Tag color="blue" icon={<CheckCircleOutlined />}>
              Personalizado
            </Tag>
          ) : (
            <Tag icon={<InfoCircleOutlined />}>Por defecto</Tag>
          )}
          {record.isSystem && (
            <Tag color="red">Sistema</Tag>
          )}
        </Space>
      ),
    },
  ];

  // Group settings by category
  const settingsByCategory = settings?.reduce((acc, setting) => {
    const category = setting.category || 'General';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(setting);
    return acc;
  }, {} as Record<string, SettingWithValue[]>) || {};

  const tabItems = Object.entries(settingsByCategory).map(([category, categorySettings]) => {
    const customCount = categorySettings.filter(s => s.hasCustomValue).length;
    return {
      key: category,
      label: (
        <Space>
          <SettingOutlined />
          {category}
          <Tag>{categorySettings.length}</Tag>
          {customCount > 0 && (
            <Tag color="blue">{customCount} mod.</Tag>
          )}
        </Space>
      ),
      children: (
        <Table
          dataSource={categorySettings}
          columns={columns}
          rowKey="id"
          pagination={false}
          size={isMobile ? 'small' : 'middle'}
          scroll={{ x: 900 }}
          rowClassName={(record) => record.hasCustomValue ? 'ant-table-row-selected' : ''}
        />
      ),
    };
  });

  if (isLoading) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 10 }} />
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Alert
          type="error"
          message="Error al cargar los settings"
          description="No se pudieron cargar los settings del sistema. Por favor intenta de nuevo."
          showIcon
          action={
            <Button onClick={() => refetch()}>Reintentar</Button>
          }
        />
      </Card>
    );
  }

  const totalSettings = settings?.length || 0;
  const customSettings = settings?.filter(s => s.hasCustomValue).length || 0;

  return (
    <div>
      <Card
        title={
          <Space>
            <SettingOutlined />
            <Title level={4} style={{ margin: 0 }}>
              Settings del Sistema
            </Title>
            <Tag color="default">{totalSettings} settings</Tag>
            {customSettings > 0 && (
              <Tag color="blue">{customSettings} personalizados</Tag>
            )}
          </Space>
        }
        extra={
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
            Actualizar
          </Button>
        }
      >
        <Alert
          type="info"
          message="Panel de Soporte"
          description={
            <span>
              Este panel permite modificar los settings del sistema para este restaurante.
              Los settings con valor <Tag color="blue" style={{ margin: '0 4px' }}>Personalizado</Tag>
              han sido modificados del valor por defecto.
              Usa el botón <Text type="danger">Reset</Text> para volver al valor original.
            </span>
          }
          showIcon
          style={{ marginBottom: 16 }}
        />

        {tabItems.length > 0 ? (
          <Tabs items={tabItems} />
        ) : (
          <Alert
            type="warning"
            message="Sin settings"
            description="No hay settings configurados para este restaurante."
            showIcon
          />
        )}
      </Card>
    </div>
  );
};

export default SystemSettings;
